import { VenueReservation } from "@/graphql/operations";
import useLocalization from "@/hooks/useLocalization";
import { reservationHasPassed } from "@/hooks/useReservation";
import useVenue from "@/hooks/useVenue";
import { dateStringToComponents } from "@/tools/date/conversion";
import icons from "@/tools/icons";
import { Stack } from "@mui/material";
import { format } from "date-fns";
import { useMemo } from "react";
import { PrimaryAndSecondaryElement } from "./PrimaryAndSecondaryElement";

type DateTimeSummaryProps = { reservation: VenueReservation };
export default function DateTimeVenueSummary({
  reservation,
}: DateTimeSummaryProps) {
  const { t, adapterLocale } = useLocalization();
  const mainGuide = useMemo(
    () => reservation.group?.guideAssignments?.[0],
    [reservation.group?.guideAssignments],
  );

  const { productMap, hasSingleVenue } = useVenue();
  const defaultVenueName = useMemo(
    () =>
      productMap.get(reservation.group?.product?.id ?? "")?.defaultVenue?.name,
    [productMap, reservation.group],
  );

  const reservationDateTimeSummary = t(
    "reservation:voiceover.reservationDateTimeSummary",
    {
      interpolation: {
        date: reservation.group!.date,
        time: reservation.reservationTime?.slice(0, 5),
      },
    },
  );

  return (
    <PrimaryAndSecondaryElement
      sx={{ flexGrow: 0, width: "200px" }}
      shouldDimSecondary={
        reservationHasPassed(reservation) ||
        !!reservation.venue?.id ||
        !!reservation.answer
      }
      aria-label={reservationDateTimeSummary}
      data-testid="date-time-venue-summary"
      primary={
        <Stack direction="row" justifyContent="space-between">
          <div>
            {t("date:date", {
              interpolation: dateStringToComponents(reservation.group!.date),
            })}
          </div>
          <div>
            {format(new Date(reservation.group!.date), "EEE", {
              locale: adapterLocale,
            })}{" "}
            {reservation.reservationTime?.slice(0, 5)}
          </div>
        </Stack>
      }
      secondary={
        hasSingleVenue
          ? (mainGuide?.guide?.name ?? t("reservation:noGuideAssigned"))
          : (reservation.venue?.name ??
            defaultVenueName ?? (
              <Stack direction="row" alignItems="center">
                {!reservation.answer && <icons.NoLocation />}{" "}
                {t("reservation:venueUndecided")}
              </Stack>
            ))
      }
    />
  );
}
